import React from "react";
import { Link } from "react-router-dom";
import { buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getCurrentSubscription } from "@/services/subscriptionService";

interface SubscriptionBadgeProps {
  className?: string;
  onClick?: () => void;
}

const SubscriptionBadge = ({ className, onClick }: SubscriptionBadgeProps) => {
  const [tier, setTier] = React.useState<string>('free');

  // Load subscription from localStorage
  const loadSubscription = React.useCallback(() => {
    const subscription = JSON.parse(localStorage.getItem('subscription') || '{"tier":"free"}');
    setTier(subscription.tier || 'free');
  }, []);

  // Fetch fresh subscription data on mount
  React.useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const subscription = await getCurrentSubscription();
        if (subscription) {
          const newTier = subscription.tier || 'free';
          setTier(newTier);
          localStorage.setItem('subscription', JSON.stringify({
            tier: newTier,
            status: subscription.status
          }));
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
        // Fall back to localStorage
        loadSubscription();
      }
    };

    fetchSubscription();
  }, [loadSubscription]);

  React.useEffect(() => {
    // Listen for storage changes (cross-tab updates)
    window.addEventListener('storage', loadSubscription);
    
    // Custom event for same-tab updates
    window.addEventListener('subscriptionUpdated', loadSubscription);

    return () => {
      window.removeEventListener('storage', loadSubscription);
      window.removeEventListener('subscriptionUpdated', loadSubscription);
    };
  }, [loadSubscription]);

  const getBadgeText = () => {
    switch(tier) {
      case 'starter':
        return 'Starter';
      case 'pro':
        return 'Pro';
      case 'unlimited':
        return 'Unlimited';
      default:
        return 'Free';
    }
  };

  return (
    <Link
      to="/subscription"
      className={`${buttonVariants({ variant: "ghost" })} ${className || ""}`}
      onClick={onClick}
    >
      <Badge className="mr-2" variant="outline">
        {getBadgeText()}
      </Badge>
      Subscription
    </Link>
  );
};

export default SubscriptionBadge;
